import { CurrencyHelpers } from 'helpers/currencyHelpers';
import useClassNames from 'helpers/hooks/useClassNames';
import usePath from 'helpers/hooks/usePath';
import dynamic from 'next/dynamic';
import { useParams, useRouter } from 'next/navigation';
import { FC, useMemo } from 'react';
import { LineItem } from 'shared/types/wishlist/LineItem';
import { ProductDetailsProps } from '..';
const Typography = dynamic(() => import('components/commercetools-ui/atoms/typography'));
const WishlistButton = dynamic(
  () => import('components/commercetools-ui/organisms/wishlist/components/wishlist-button'),
);
const ProductVariant = dynamic(() => import('./product-variant'));

type ProductInformationProps = Omit<ProductDetailsProps, 'onAddToCart'> & {
  priceLabel?: string;
};

const ProductInformation: FC<ProductInformationProps> = ({
  product,
  variant,
  onChangeVariant,
  inModalVersion,
  priceLabel,
}) => {
  const router = useRouter();
  const { locale } = useParams();
  const { path } = usePath();

  const attributesToDisplay = ['color', 'finish', 'size'];

  const discountPercentage =
    variant.discountedPrice &&
    (((variant.price?.centAmount as number) - (variant.discountedPrice.centAmount as number)) /
      (variant.price?.centAmount as number)) *
      100;

  const updateVariantSKU = (sku: string) => {
    router.replace(`/${path.split('/').filter(Boolean).slice(0, -1).join('/')}/${sku}`);
  };

  const productToWishlistLineItem = useMemo<LineItem>(() => {
    return {
      lineItemId: product?.productId ?? '',
      productId: product?.productId,
      name: product?.name,
      count: 1,
      variant: variant,
      addedAt: new Date(),
      _url: product?._url,
    };
  }, [product, variant]);

  const isCertificationCard = product?.productType === 'certification-card';

  const titleClassName = useClassNames(['break-normal mb-12', { '2xl:text-18': !inModalVersion }]);
  const priceClassName = useClassNames([
    'block leading-loose text-18',
    { 'line-through text-gray-500': !!discountPercentage },
    // { '2xl:text-20': !inModalVersion },
    // { 'text-14': !!inModalVersion && !discountPercentage },
    // { 'text-12': !!inModalVersion && !!discountPercentage },
  ]);
  const discountedPriceClassName = useClassNames(['m-0 block text-18 font-bold leading-loose text-accent-red']);

  return (
    <div data-id="product-information">
      <div data-id="product-title-wrapper" className="relative flex pr-40">
        <Typography data-id="product-title" as="h1" className={`mb-20 text-xl font-bold leading-loose md:text-3xl`}>
          {product?.name}
        </Typography>

        {!isCertificationCard && <WishlistButton lineItem={productToWishlistLineItem} dataId="wishlist-button" />}
      </div>

      {variant.price &&
        (discountPercentage ? (
          <div data-id="price-with-discount" className="flex flex-row justify-between">
            <div data-id="price-container" className="flex items-center gap-8">
              <Typography data-id="original-price" className={priceClassName}>
                {CurrencyHelpers.formatForCurrency(variant.price ?? 0, locale) + (priceLabel ? ' ' + priceLabel : '')}
              </Typography>
              <Typography data-id="discounted-price" className={discountedPriceClassName}>
                {CurrencyHelpers.formatForCurrency(variant.discountedPrice as number, locale) +
                  (priceLabel ? ' ' + priceLabel : '')}
              </Typography>
            </div>

            {/* {
              <span className="mb-8 ml-8 mt-10 flex h-[25px] w-[45px] items-center justify-center bg-accent-red text-11 text-neutral-100">
                {Math.round(discountPercentage)}%
              </span>
            } */}
          </div>
        ) : (
          <Typography data-id="regular-price" className={priceClassName}>
            {CurrencyHelpers.formatForCurrency(variant.price as number, locale) + (priceLabel ? ' ' + priceLabel : '')}
          </Typography>
        ))}

      {attributesToDisplay.map((attribute) => {
        if (variant?.attributes?.[attribute]) {
          return (
            <ProductVariant
              key={attribute}
              className="mt-20 border-b border-b-neutral-400 pb-20 md:mt-24"
              variants={product?.variants}
              currentVariant={variant}
              attribute={attribute}
              inModalVersion={inModalVersion}
              onClick={inModalVersion ? onChangeVariant : updateVariantSKU}
            />
          );
        }
      })}
    </div>
  );
};

export default ProductInformation;
